/**
 * MemoryManager - RAM management service
 * Handles single-instance enforcement, tab visibility pause/resume,
 * periodic memory sweeps, and memory budget monitoring.
 */

import { EventBus } from '../utils/EventBus';
import { ServiceLocator, Services } from '../utils/ServiceLocator';
import type { MMONetworkManager } from './MMONetworkManager';
import type { IDisposable } from '../shared/common/lifecycle';

const MEMORY_WARNING_MB = 512;
const MEMORY_CRITICAL_MB = 768;
const MEMORY_CHECK_INTERVAL = 10_000; // 10s
const SWEEP_INTERVAL = 30_000; // 30s
const INSTANCE_LOCK_KEY = 'crimsonmandate_instance_lock';
const INSTANCE_HEARTBEAT_INTERVAL = 2_000; // 2s

export const MemoryEvents = {
  VISIBILITY_CHANGED: 'memory-visibility-changed',
  MEMORY_WARNING: 'memory-warning',
  MEMORY_CRITICAL: 'memory-critical',
  SWEEP_TRIGGERED: 'memory-sweep-triggered',
  INSTANCE_BLOCKED: 'memory-instance-blocked',
} as const;

export class MemoryManager implements IDisposable {
  private _isDisposed = false;
  private game: Phaser.Game | null = null;
  private broadcastChannel: BroadcastChannel | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private memoryCheckTimer: ReturnType<typeof setInterval> | null = null;
  private sweepTimer: ReturnType<typeof setInterval> | null = null;
  private visibilityHandler: (() => void) | null = null;
  private instanceId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  private isVisible = true;

  get isDisposed(): boolean {
    return this._isDisposed;
  }

  /**
   * Attempt to acquire single-instance lock.
   * Returns false if another tab already has the lock.
   */
  acquireInstanceLock(): boolean {
    // Try BroadcastChannel first
    try {
      this.broadcastChannel = new BroadcastChannel('crimsonmandate_instance');
      this.broadcastChannel.onmessage = (event) => {
        if (event.data?.type === 'instance_check') {
          // Another tab is checking — respond to block it
          this.broadcastChannel?.postMessage({ type: 'instance_exists', id: this.instanceId });
        }
      };

      // Send a check and wait synchronously via localStorage
      // (BroadcastChannel is async, so we use localStorage as the sync fallback)
    } catch {
      // BroadcastChannel not supported, fall through to localStorage
    }

    // localStorage heartbeat lock
    const lockData = localStorage.getItem(INSTANCE_LOCK_KEY);
    if (lockData) {
      try {
        const parsed = JSON.parse(lockData);
        const age = Date.now() - parsed.timestamp;
        // Check if the lock belongs to the same sessionStorage context (same tab reload)
        const prevSessionId = sessionStorage.getItem('crimsonmandate_session_id');
        const isSameTab = prevSessionId && parsed.id === prevSessionId;
        if (age < INSTANCE_HEARTBEAT_INTERVAL * 3 && !isSameTab) {
          // Lock is still fresh AND belongs to a different tab — block
          return false;
        }
      } catch {
        // Corrupt lock data, take over
      }
    }

    // Acquire lock and record instance ID in sessionStorage for reload detection
    this.writeLock();
    sessionStorage.setItem('crimsonmandate_session_id', this.instanceId);
    this.heartbeatTimer = setInterval(() => this.writeLock(), INSTANCE_HEARTBEAT_INTERVAL);

    // Release lock on unload
    window.addEventListener('beforeunload', () => {
      localStorage.removeItem(INSTANCE_LOCK_KEY);
      this.broadcastChannel?.close();
    });

    return true;
  }

  private writeLock(): void {
    localStorage.setItem(
      INSTANCE_LOCK_KEY,
      JSON.stringify({ id: this.instanceId, timestamp: Date.now() })
    );
  }

  /**
   * Initialize with the Phaser game instance.
   * Sets up visibility handling, memory monitoring, and periodic sweeps.
   */
  initialize(game: Phaser.Game): void {
    this.game = game;
    this.setupVisibilityHandling();

    // Periodic memory sweep
    this.sweepTimer = setInterval(() => {
      EventBus.emit(MemoryEvents.SWEEP_TRIGGERED);
    }, SWEEP_INTERVAL);

    // Memory budget monitoring (Chrome only)
    if ((performance as unknown as { memory?: unknown }).memory) {
      this.memoryCheckTimer = setInterval(() => this.checkMemoryBudget(), MEMORY_CHECK_INTERVAL);
    }
  }

  private setupVisibilityHandling(): void {
    this.visibilityHandler = () => {
      const visible = document.visibilityState === 'visible';
      if (visible === this.isVisible) return;
      this.isVisible = visible;

      if (visible) {
        this.resumeGame();
      } else {
        this.pauseGame();
      }

      // Notify server
      try {
        const network = ServiceLocator.tryGet<MMONetworkManager>(Services.MMO_NETWORK);
        if (network) {
          network.sendVisibilityChange(visible);
        }
      } catch {
        // Network not available yet
      }

      EventBus.emit(MemoryEvents.VISIBILITY_CHANGED, { visible });
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private pauseGame(): void {
    if (!this.game) return;
    // Pause all active scenes to stop rendering
    this.game.scene.getScenes(true).forEach((scene) => {
      if (scene.scene.key !== 'BootScene' && scene.scene.key !== 'PreloadScene') {
        scene.scene.pause();
      }
    });
    console.log('[MemoryManager] Game paused (tab hidden)');
  }

  private resumeGame(): void {
    if (!this.game) return;
    // Resume all paused scenes
    this.game.scene.getScenes(false).forEach((scene) => {
      if (scene.scene.isPaused()) {
        scene.scene.resume();
      }
    });

    // Trigger a sweep on resume to clean up stale data
    EventBus.emit(MemoryEvents.SWEEP_TRIGGERED);
    console.log('[MemoryManager] Game resumed (tab visible)');
  }

  private checkMemoryBudget(): void {
    const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
    if (!mem) return;

    const usedMB = mem.usedJSHeapSize / (1024 * 1024);

    if (usedMB > MEMORY_CRITICAL_MB) {
      console.warn(`[MemoryManager] CRITICAL: ${usedMB.toFixed(0)}MB heap usage`);
      EventBus.emit(MemoryEvents.MEMORY_CRITICAL, { usedMB });
    } else if (usedMB > MEMORY_WARNING_MB) {
      console.warn(`[MemoryManager] WARNING: ${usedMB.toFixed(0)}MB heap usage`);
      EventBus.emit(MemoryEvents.MEMORY_WARNING, { usedMB });
    }
  }

  getStats(): { usedMB: number; isVisible: boolean; instanceId: string } {
    const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
    return {
      usedMB: mem ? mem.usedJSHeapSize / (1024 * 1024) : 0,
      isVisible: this.isVisible,
      instanceId: this.instanceId,
    };
  }

  dispose(): void {
    if (this._isDisposed) return;
    this._isDisposed = true;

    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.memoryCheckTimer) clearInterval(this.memoryCheckTimer);
    if (this.sweepTimer) clearInterval(this.sweepTimer);
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    if (this.broadcastChannel) this.broadcastChannel.close();
    localStorage.removeItem(INSTANCE_LOCK_KEY);

    this.game = null;
  }
}
